"use client"

const REPORT_COOLDOWN_MS = 60 * 1000
const REPORT_WINDOW_MS = 10 * 60 * 1000
const MAX_REPORTS_PER_WINDOW = 3
const STORAGE_PREFIX = 'client-error-report-limit:'

const memoryRecords = new Map<string, ErrorReportRecord>()

type ErrorReportRecord = {
  windowStart: number
  count: number
  lastRecordAt: number
}

type ErrorLike = {
  message?: string
  digest?: string
}

export function shouldReportClientError(error: ErrorLike, type: string) {
  const now = Date.now()
  const key = `${STORAGE_PREFIX}${hashString(`${type}|${error.message || ''}|${error.digest || ''}`)}`
  const record = readRecord(key)

  if (!record || now - record.windowStart >= REPORT_WINDOW_MS) {
    writeRecord(key, {
      windowStart: now,
      count: 1,
      lastRecordAt: now,
    })
    return true
  }

  if (now - record.lastRecordAt < REPORT_COOLDOWN_MS) {
    return false
  }

  if (record.count >= MAX_REPORTS_PER_WINDOW) {
    return false
  }

  writeRecord(key, {
    ...record,
    count: record.count + 1,
    lastRecordAt: now,
  })
  return true
}

function readRecord(key: string): ErrorReportRecord | null {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return memoryRecords.get(key) || null
    const parsed = JSON.parse(raw) as ErrorReportRecord
    if (
      typeof parsed.windowStart !== 'number' ||
      typeof parsed.count !== 'number' ||
      typeof parsed.lastRecordAt !== 'number'
    ) {
      return null
    }
    return parsed
  } catch {
    return memoryRecords.get(key) || null
  }
}

function writeRecord(key: string, record: ErrorReportRecord) {
  memoryRecords.set(key, record)
  try {
    window.localStorage.setItem(key, JSON.stringify(record))
  } catch {
    // localStorage may be unavailable in private mode or restricted browsers.
  }
}

function hashString(input: string) {
  let hash = 5381
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash) ^ input.charCodeAt(i)
  }
  return (hash >>> 0).toString(36)
}
